import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from './actions'
import {
  CreateSimpleTaskType,
  InitialState,
  ModalCreateDataType,
  UpdateBlockDataType,
} from './types'
import usePrevious from '../../hooks/usePrevious'
import { Modal } from 'react-responsive-modal'

// Components
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import AdminBlocks from '../../components/AdminBlocks'
import Loading from '../../components/Loading'
import AdminSimpleTaskEditor from '../../components/AdminSimpleTaskEditor'
import AdminNotionBlock from '../../components/AdminNotionBlock'

// Modal styles
const customModalStyles = {
  root: {
    zIndex: 99999,
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.4)',
  },
  modal: {
    width: 400,
    borderRadius: 20,
    padding: 20,
    margin: '2em',
    boxShadow: 'unset',
    overflow: 'unset',
    outline: 0,
    backgroundColor: '#FFF',
  },
}

export default () => {
  const { id, mid, lid } = useParams<{ id: string; mid: string; lid: string }>()

  const history = useHistory()

  const initialModalCreateData: ModalCreateDataType = {
    modal: false,
    type: 'notion',
    caption: 'Полное описание урока',
    tag: '',
    payload: '',
    module: '',
  }

  const [modalCreateData, setModalCreateData] = useState<ModalCreateDataType>(
    initialModalCreateData
  )
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [notionModal, setNotionModal] = useState<boolean>(false)
  const [simpleTaskModal, setSimpleTaskModal] = useState<boolean>(false)
  const [simpleTaskBlockId, setSimpleTaskBlockId] = useState<null | string>(
    null
  )
  const [selectedNotionId, setSelectedNotionId] = useState<string>('')

  const createIsNotion =
    modalCreateData.type === 'notion' || modalCreateData.type === 'short'

  const createIsTask = modalCreateData.type === 'simple-task'

  const refPayload = useRef<HTMLInputElement | null>(null)

  const dispatch = useDispatch()
  const isMounted = useRef<boolean>(false)

  const {
    blocks,
    modules,
    processIsFetching,
    processIsUpdateBlock,
    processIsSortBlocks,
    processIsDeleteBlock,
    processIsCreateBlock,
    processIsFetchingNotion,
    error,
  } = useSelector(
    (state: { adminLessonBlocks: InitialState }) => state.adminLessonBlocks
  )

  const prevProcessIsFetching = usePrevious(processIsFetching)

  useEffect(() => {
    if (!processIsFetching && prevProcessIsFetching) {
      setIsLoaded(true)
    }
  }, [processIsFetching])

  useEffect(() => {
    dispatch(actions.destroy())
    dispatch(actions.fetchLessonBlocks(lid))
    dispatch(actions.fetchModules(id))
    isMounted.current = true
    return () => {
      dispatch(actions.destroy())
    }
  }, [])

  const onReloadBlocks = () => {
    dispatch(actions.fetchLessonBlocks(lid))
  }

  const onUpdateNotion = (block_id: string, notion_page_id: string) => {
    const data = { payload: { notion_page_id } }
    dispatch(actions.updateBlock(lid, block_id, data))
    setNotionModal(false)
  }

  const handleUpdateBlock = (
    block_id: string,
    data: UpdateBlockDataType
  ): void => {
    dispatch(actions.updateBlock(lid, block_id, data))
  }

  const handleSortBlocks = (ids: string[]): void => {
    dispatch(actions.sortBlocks(lid, ids))
  }

  const handleDeleteBlock = (block_id: string): void => {
    dispatch(actions.deleteBlock(lid, block_id))
  }

  const handleOpenCreateModal = () => {
    setModalCreateData({ ...initialModalCreateData, modal: true })
  }

  const handleTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const type = event.currentTarget.value
    const caption: { [key: string]: string } = {
      notion: 'Полное описание урока',
      short: 'Короткое описание урока',
      links: 'Сопутствующие ссылки',
      files: 'Файлы',
      videos: 'Видео',
      gallery: 'Галерея',
      'simple-task': 'Задания урока',
    }

    if (type === 'simple-task') {
      setModalCreateData({
        ...modalCreateData,
        modal: false,
      })
      setSimpleTaskModal(true)
      setSimpleTaskBlockId(null)
    } else {
      setModalCreateData({
        ...modalCreateData,
        type,
        caption: caption[type],
        payload: '',
        tag: type === 'short' ? 'short' : '',
      })
    }
  }

  const handleNotionIdChange = (event: ChangeEvent<HTMLInputElement>) => {
    setModalCreateData({
      ...modalCreateData,
      payload: event.target.value,
    })
  }

  const handleCreateBlock = () => {
    if ((createIsNotion || createIsTask) && !modalCreateData.payload) {
      if (refPayload.current) {
        refPayload.current?.focus()
      }
      return
    }
    const { type, caption, payload, tag, module } = modalCreateData
    const position = blocks.length + 1
    const formatType = type === 'short' ? 'notion' : type
    const data = {
      type: formatType,
      tag: tag || undefined,
      caption,
      payload: {
        ...(formatType === 'notion' ? { notion_page_id: payload } : {}),
        ...(formatType === 'simple-task'
          ? { simple_task_id: payload, module_id: module }
          : {}),
      },
    }

    dispatch(actions.createBlock(lid, data, position))

    setModalCreateData({ ...initialModalCreateData })
  }

  const handleCreateSimpleTask = (data: CreateSimpleTaskType) => {
    const {
      type,
      module_id,
      notion_description_id,
      caption,
      description,
      show_field,
      field_caption,
      field_required,
      show_file,
      file_caption,
      file_required,
      task_required,
    } = data
    const position = blocks.length + 1
    dispatch(
      actions.createSimpleTask(
        lid,
        position,
        module_id,
        type,
        notion_description_id,
        caption,
        description,
        show_field,
        field_caption,
        field_required,
        show_file,
        file_caption,
        file_required,
        task_required
      )
    )
    setSimpleTaskModal(false)
  }

  const handleUpdateSimpleTask = (
    block_id: string,
    data: CreateSimpleTaskType
  ) => {
    const {
      type,
      module_id,
      notion_description_id,
      caption,
      description,
      show_field,
      field_caption,
      field_required,
      show_file,
      file_caption,
      file_required,
      task_required,
    } = data
    const simpleTaskIds =
      blocks.find(block => block.block_id === simpleTaskBlockId)?.simple_task
        ?.simple_task_id || ''
    dispatch(
      actions.updateSimpleTask(
        lid,
        module_id,
        simpleTaskIds,
        block_id,
        type,
        notion_description_id,
        caption,
        description,
        show_field,
        field_caption,
        field_required,
        show_file,
        file_caption,
        file_required,
        task_required
      )
    )
    setSimpleTaskModal(false)
  }

  const handleSyncNotion = (simple_task_id: string) => {
    dispatch(actions.fetchSimpleTaskNotion(simple_task_id))
  }

  const handleNotionFetch = (block_id: string, block_notion_id: string) => {
    dispatch(actions.fetchNotion(block_id, block_notion_id))
  }

  return (
    <>
      <Header />
      <div className={'container'}>
        <div className={'admin-blocks__header'}>
          <h2>Панель администрирования - Редактирование блоков урока</h2>
          {processIsFetching ||
          processIsUpdateBlock ||
          processIsSortBlocks ||
          processIsDeleteBlock ||
          processIsCreateBlock ? (
            <Loading
              style={{ margin: 0, marginLeft: 10 }}
              width={30}
              height={30}
              color={'#1978B2'}
            />
          ) : null}
        </div>
        {(!isMounted.current || processIsFetching) && !isLoaded ? (
          <p>Загрузка блоков...</p>
        ) : (
          <>
            <AdminBlocks
              blocks={blocks}
              onSort={handleSortBlocks}
              onChange={handleUpdateBlock}
              onDelete={handleDeleteBlock}
              onClick={block_id => {
                const blockType = blocks.find(
                  block => block.block_id === block_id
                )
                if (blockType && blockType.type === 'simple-task') {
                  setSimpleTaskBlockId(blockType.block_id)
                  setSimpleTaskModal(true)
                } else {
                  if (blockType && blockType.type === 'notion') {
                    setSelectedNotionId(blockType.block_id)
                    setNotionModal(true)
                  } else {
                    history.push(
                      `/admin/courses/${id}/modules/${mid}/lessons/${lid}/blocks/${block_id}`
                    )
                  }
                }
              }}
              isProcess={
                processIsFetching || processIsUpdateBlock || processIsSortBlocks
              }
            />
            <button
              onClick={handleOpenCreateModal}
              style={{ margin: '10px 0' }}
            >
              Добавить блок
            </button>
          </>
        )}
        {error ? <p style={{ color: 'red' }}>{error}</p> : null}
      </div>
      <Modal
        open={modalCreateData.modal}
        onClose={() => setModalCreateData(initialModalCreateData)}
        styles={customModalStyles}
        center={true}
      >
        <h3 style={{ margin: 0, marginBottom: 10 }}>Добавить блок</h3>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor={'types'}>Доступные типы</label>
          <select
            style={{ margin: '5px 0 10px', outline: 0 }}
            value={modalCreateData.type}
            onChange={handleTypeChange}
          >
            <option value={'notion'}>Описание урока</option>
            <option value={'short'}>Короткое описание урока</option>
            <option value={'links'}>Ссылки</option>
            <option value={'files'}>Файлы</option>
            <option value={'videos'}>Видео</option>
            <option value={'gallery'}>Галерея</option>
            <option value={'simple-task'}>Задания</option>
          </select>
          {createIsNotion ? (
            <>
              <label htmlFor={'notion'}>Notion Page ID</label>
              <input
                style={{ margin: '5px 0 10px', outline: 0 }}
                id={'notion'}
                type={'text'}
                placeholder={''}
                value={modalCreateData.payload}
                onChange={handleNotionIdChange}
                ref={refPayload}
                autoComplete={'off'}
              />{' '}
            </>
          ) : null}
          <button onClick={handleCreateBlock}>Добавить</button>
        </div>
      </Modal>
      <AdminNotionBlock
        modal={notionModal}
        blocks={blocks}
        notionId={selectedNotionId}
        isFetching={processIsFetchingNotion}
        onFetch={handleNotionFetch}
        onClose={setNotionModal}
        onReloadBlocks={onReloadBlocks}
        onUpdate={onUpdateNotion}
      />
      <AdminSimpleTaskEditor
        modal={simpleTaskModal}
        modules={modules}
        blocks={blocks}
        module_id={mid}
        simpleTaskBlockId={simpleTaskBlockId}
        onCreate={handleCreateSimpleTask}
        onUpdate={handleUpdateSimpleTask}
        onSync={handleSyncNotion}
        onClose={() => setSimpleTaskModal(false)}
      />
      <Footer />
    </>
  )
}
